// src/components/RoomCard.jsx

import React, { useState, useEffect } from "react";
import { 
  Card, 
  CardContent, 
  CardMedia, 
  Typography, 
  Box, 
  Button, 
  Stack,
  useTheme
} from "@mui/material";
import { 
  PeopleOutline as PeopleOutlineIcon,
  Image as ImageIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getRoomFields } from "../services/roomFieldService";

function RoomCard({ 
  room, 
  adminMode = false, 
  onEdit = null, 
  onDelete = null 
}) {
  const [roomFields, setRoomFields] = useState([]);
  const [fieldsLoaded, setFieldsLoaded] = useState(false);
  
  // Get user from Redux store and theme
  const user = useSelector((state) => state.user.user);
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    // Only load fields if user is authenticated
    if (user) {
      loadRoomFields();
    } else {
      setFieldsLoaded(true); // Don't try to load if not authenticated
    }
  }, [user]);

  const loadRoomFields = async () => {
    try {
      const fields = await getRoomFields();
      setRoomFields(fields || []);
    } catch (error) {
      console.error('Error loading room fields for display:', error);
    } finally {
      setFieldsLoaded(true);
    }
  };

  const renderFieldValue = (field, value) => {
    if (value === undefined || value === null || value === '') {
      return null;
    }

    switch (field.type) {
      case 'boolean':
        return value === true || value === 'true' ? 'Yes' : 'No';
      case 'select':
        return value;
      case 'number':
        return value.toString();
      default:
        return value;
    }
  };

  // Render room fields dynamically
  const renderRoomFields = () => {
    if (!fieldsLoaded || !roomFields.length) {
      // Fallback for when no fields are configured
      const fallbackFields = [];
      if (room.capacity) {
        fallbackFields.push({
          label: 'Capacity',
          value: `${room.capacity} people`
        });
      }
      if (room.location) {
        fallbackFields.push({
          label: 'Location',
          value: room.location
        });
      }
      return fallbackFields;
    }

    return roomFields
      .sort((a, b) => a.order - b.order)
      .map(field => {
        let value;
        
        if (field.isSystem) {
          value = room[field.name];
        } else {
          const customFields = room.customFields instanceof Map 
            ? Object.fromEntries(room.customFields)
            : room.customFields;
          value = customFields?.[field.name];
        }

        const displayValue = renderFieldValue(field, value);
        
        if (!displayValue) return null;

        return {
          label: field.label,
          value: displayValue
        };
      })
      .filter(Boolean);
  };

  // Get the first image from the room's images array
  const roomImage = room.images && room.images.length > 0 ? room.images[0] : null;

  const handleBookRoom = () => {
    if (room?._id) {
      navigate(`/book-room/${room._id}`);
    } else {
      console.error('Room ID not available for booking');
    }
  };

  const handleRoomInfo = () => {
    if (room?._id) {
      navigate(`/book-room/${room._id}#about`);
    } else {
      console.error('Room ID not available for info');
    }
  };

  return (
    <Card
      sx={{
        maxWidth: 390,
        minWidth: 320,
        height: "100%", // Take full height of grid cell
        borderRadius: theme.shape.borderRadius * 1.5,
        border: `1px solid ${theme.palette.divider}`,
        p: { xs: 1, sm: 1.25 },
        pb: { xs: 1, sm: 1.25 },
        display: "flex",
        flexDirection: "column", // Stack content vertically
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          height: "100%", // Take full height
        }}
      >
        {/* Room Image */}
        {roomImage ? (
          <CardMedia
            component="img"
            sx={{
              height: { xs: 180, sm: 228 }, // Responsive height
              width: "100%",
              borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
              objectFit: 'cover',
              backgroundColor: theme.palette.primary.light + '1A', // Light blue with 10% opacity
            }}
            image={roomImage.url || roomImage.thumbnailUrl}
            alt={`${room.name} image`}
          />
        ) : (
          <Box
            sx={{
              height: { xs: 180, sm: 228 }, // Responsive height
              width: "100%",
              backgroundColor: theme.palette.primary.light + '1A', // Light blue with 10% opacity
              borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.palette.text.secondary
            }}
          >
            <ImageIcon sx={{ fontSize: 48 }} />
          </Box>
        )}

        <CardContent sx={{ 
          p: 0, 
          pl: 1, 
          pt: 2.5, // Add top padding for spacing from image
          width: "100%", 
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between" // Push buttons to bottom
        }}>
          {/* Top content: Title and Fields */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h5"
              sx={{
                fontFamily: theme.typography.fontFamily,
                fontWeight: theme.typography.h5.fontWeight,
                color: theme.palette.text.primary,
                fontSize: theme.typography.h5.fontSize,
                mb: 2.5,
                wordWrap: 'break-word',
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {room.name}
            </Typography>

            <Stack spacing={1.5} sx={{ pl: 0.5, width: "100%", mb: 2.5 }}>
            {/* Dynamic Room Fields */}
            {renderRoomFields().map((field, index) => (
              <Stack key={index} direction="row" spacing={1.5} alignItems="flex-start">
                <PeopleOutlineIcon
                  sx={{ 
                    color: theme.palette.text.secondary, 
                    width: 24, 
                    height: 15,
                    flexShrink: 0,
                    mt: 0.25 // Slight offset to align with text
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: theme.typography.fontFamily,
                    fontWeight: theme.typography.body1.fontWeight,
                    color: theme.palette.text.secondary,
                    fontSize: theme.typography.body1.fontSize,
                    flex: 1,
                    wordWrap: 'break-word',
                    overflow: 'hidden',
                  }}
                >
                  {field.label}: {field.value}
                </Typography>
              </Stack>
            ))}
          </Stack>
          </Box>

          {/* Bottom content: Action buttons */}
          {adminMode ? (
            // Admin Mode: Edit and Delete buttons
            <Stack 
              direction="row"  
              alignItems="center" 
              justifyContent="space-between"
              sx={{
                flexWrap: 'wrap',
                gap: 1,
                minHeight: 43,
              }}
            >
              <Button
                variant="contained"
                onClick={() => onEdit && onEdit(room)}
                startIcon={<EditIcon />}
                sx={{
                  minWidth: 120,
                  height: 43,
                  flex: '1 1 auto',
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: theme.shape.borderRadius * 0.5,
                  fontFamily: theme.typography.fontFamily,
                  fontWeight: theme.typography.button.fontWeight,
                  fontSize: theme.typography.button.fontSize,
                  textTransform: theme.typography.button.textTransform,
                  transition: theme.transitions.create(
                    ['background-color', 'transform'],
                    { duration: theme.transitions.duration.short }
                  ),
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                    transform: "translateY(-1px)",
                  },
                  "&:active": {
                    transform: "translateY(0px)",
                  },
                }}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                onClick={() => onDelete && onDelete(room)}
                startIcon={<DeleteIcon />}
                sx={{
                  minWidth: 120,
                  height: 43,
                  flex: '1 1 auto',
                  borderColor: theme.palette.error.main,
                  color: theme.palette.error.main,
                  borderRadius: theme.shape.borderRadius * 0.5,
                  fontFamily: theme.typography.fontFamily,
                  fontWeight: theme.typography.button.fontWeight,
                  fontSize: theme.typography.button.fontSize,
                  textTransform: theme.typography.button.textTransform,
                  transition: theme.transitions.create(
                    ['background-color', 'transform', 'color'],
                    { duration: theme.transitions.duration.short }
                  ),
                  "&:hover": {
                    backgroundColor: theme.palette.error.main,
                    color: theme.palette.error.contrastText,
                    transform: "translateY(-1px)",
                  },
                  "&:active": {
                    transform: "translateY(0px)",
                  },
                }}
              >
                Delete
              </Button>
            </Stack>
          ) : (
                         // Regular Mode: Book Room and Room Info
             <Stack 
               direction="row"  
               alignItems="center" 
               sx={{
                 flexWrap: 'wrap',
                 gap: 1,
                 minHeight: 43,
               }}
             >
              <Button
                variant="contained"
                onClick={handleBookRoom}
                sx={{
                  minWidth: 140,
                  maxWidth: 193,
                  height: 43,
                  flex: '1 1 auto',
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: theme.shape.borderRadius * 0.5,
                  boxShadow: theme.customShadows?.primaryButton || theme.shadows[3],
                  fontFamily: theme.typography.fontFamily,
                  fontWeight: theme.typography.button.fontWeight,
                  fontSize: theme.typography.button.fontSize,
                  textTransform: theme.typography.button.textTransform,
                  transition: theme.transitions.create(
                    ['background-color', 'transform'],
                    { duration: theme.transitions.duration.short }
                  ),
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                    transform: "translateY(-1px)",
                  },
                  "&:active": {
                    transform: "translateY(0px)",
                  },
                }}
              >
                Book The Room
              </Button>
              <Typography
                onClick={handleRoomInfo}
                sx={{
                  fontFamily: theme.typography.fontFamily,
                  fontWeight: theme.typography.body1.fontWeight,
                  color: theme.palette.text.primary,
                  fontSize: theme.typography.body1.fontSize,
                  cursor: "pointer",
                  userSelect: "none",
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  transition: theme.transitions.create(
                    ['color'],
                    { duration: theme.transitions.duration.short }
                  ),
                  "&:hover": {
                    color: theme.palette.primary.main,
                  },
                  "&:active": {
                    color: theme.palette.primary.dark,
                  },
                }}
              >
                Room info
              </Typography>
            </Stack>
          )}
        </CardContent>
      </Box>
    </Card>
  );
}

export default RoomCard;
